import React, { useState, useEffect } from 'react';
import { FaSlidersH, FaTimes } from 'react-icons/fa';
import axios from 'axios';
import '../styles/Equalizer.css';

const API = process.env.REACT_APP_API_URL || '';

const PRESETS = {
  flat: { name: 'Neutre', gains: [0, 0, 0, 0, 0, 0, 0, 0] },
  bass_boost: { name: 'Bass Boost', gains: [6, 5, 3, 1, 0, 0, 0, 0] },
  treble_boost: { name: 'Aigus', gains: [0, 0, 0, 0, 1, 3, 5, 6] },
  vocal: { name: 'Voix', gains: [-2, -1, 0, 3, 4, 3, 0, -1] },
  electronic: { name: 'Électro', gains: [4, 3, 0, -2, -1, 1, 3, 4] },
  rock: { name: 'Rock', gains: [4, 3, 1, 0, -1, 1, 3, 4] },
  classical: { name: 'Classique', gains: [0, 0, 0, 0, 0, -1, -2, -4] },
  night: { name: 'Nuit', gains: [-3, -2, 0, 2, 2, 0, -2, -4] }
};

const FREQUENCIES = [60, 170, 350, 1000, 3500, 10000, 14000, 16000];

function Equalizer({ audioContext, sourceNode, onClose }) {
  const [preset, setPreset] = useState('flat');
  const [gains, setGains] = useState(PRESETS.flat.gains);
  const [filters, setFilters] = useState([]);
  const [crossfade, setCrossfade] = useState(0);

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (audioContext && sourceNode) {
      setupFilters();
    }
    return () => {
      filters.forEach(f => { try { f.disconnect(); } catch(e) {} });
    };
  }, [audioContext, sourceNode]);

  useEffect(() => {
    filters.forEach((f, i) => {
      if (f && gains[i] !== undefined) f.gain.value = gains[i];
    });
  }, [gains, filters]);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/audio-settings`);
      if (res.data.equalizer_preset) {
        setPreset(res.data.equalizer_preset);
        if (res.data.equalizer_preset === 'custom' && res.data.equalizer_custom) {
          setGains(res.data.equalizer_custom);
        } else if (PRESETS[res.data.equalizer_preset]) {
          setGains(PRESETS[res.data.equalizer_preset].gains);
        }
      }
      if (res.data.crossfade_duration !== undefined) {
        setCrossfade(res.data.crossfade_duration);
      }
    } catch (e) { /* ignore */ }
  };

  const saveSettings = async (newPreset, newGains, newCrossfade) => {
    try {
      await axios.put(`${API}/api/audio-settings`, {
        equalizer_preset: newPreset,
        equalizer_custom: newPreset === 'custom' ? newGains : null,
        crossfade_duration: newCrossfade !== undefined ? newCrossfade : crossfade
      });
    } catch (e) { /* ignore */ }
  };

  const setupFilters = () => {
    if (!audioContext || !sourceNode) return;
    const newFilters = FREQUENCIES.map((freq, i) => {
      const filter = audioContext.createBiquadFilter();
      filter.type = i === 0 ? 'lowshelf' : i === FREQUENCIES.length - 1 ? 'highshelf' : 'peaking';
      filter.frequency.value = freq;
      filter.Q.value = 1;
      filter.gain.value = gains[i];
      return filter;
    });

    // Chaîner: source → filter1 → filter2 → ... → destination
    try { sourceNode.disconnect(); } catch(e) {}
    let prev = sourceNode;
    newFilters.forEach(f => { prev.connect(f); prev = f; });
    prev.connect(audioContext.destination);
    setFilters(newFilters);
  };

  const applyPreset = (name) => {
    setPreset(name);
    const g = PRESETS[name]?.gains || PRESETS.flat.gains;
    setGains([...g]);
    saveSettings(name, g, crossfade);
  };

  const changeGain = (index, value) => {
    const newGains = [...gains];
    newGains[index] = parseFloat(value);
    setGains(newGains);
    setPreset('custom');
    saveSettings('custom', newGains, crossfade);
  };

  const changeCrossfade = (value) => {
    const v = parseInt(value);
    setCrossfade(v);
    saveSettings(preset, gains, v);
  };

  const freqLabel = (f) => f >= 1000 ? `${f/1000}k` : f;

  return (
    <div className="equalizer-panel">
      <div className="eq-header">
        <h3><FaSlidersH /> Égaliseur</h3>
        <button onClick={onClose} className="eq-close"><FaTimes /></button>
      </div>

      <div className="eq-presets">
        {Object.entries(PRESETS).map(([key, val]) => (
          <button key={key} className={`eq-preset-btn ${preset === key ? 'active' : ''}`} onClick={() => applyPreset(key)}>
            {val.name}
          </button>
        ))}
      </div>

      <div className="eq-sliders">
        {FREQUENCIES.map((freq, i) => (
          <div key={freq} className="eq-slider-col">
            <span className="eq-gain-value">{gains[i] > 0 ? '+' : ''}{gains[i]}</span>
            <input type="range" min="-12" max="12" step="1" value={gains[i]}
              onChange={e => changeGain(i, e.target.value)}
              className="eq-slider" orient="vertical" />
            <span className="eq-freq-label">{freqLabel(freq)}</span>
          </div>
        ))}
      </div>

      <div className="eq-crossfade">
        <label>Crossfade: {crossfade > 0 ? `${(crossfade/1000).toFixed(1)}s` : 'Off'}</label>
        <input type="range" min="0" max="10000" step="500" value={crossfade}
          onChange={e => changeCrossfade(e.target.value)} />
      </div>
    </div>
  );
}

export default Equalizer;
